import type { MutationOptions, QueryObserverOptions } from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import {
  changeTicketClaimStatusApi,
  changeTicketDetailsApi,
  changeTicketPriorityApi,
  closeTicketApi,
  createMultipleTicketsApi,
  createScreenshotMessageApi,
  createTicketApi,
  createTicketAttachmentApi,
  createTicketMessageApi,
  createTicketMultiAttachmentApi,
  createTicketMultiPartAttachmentCompleteApi,
  createTicketMultiPartAttachmentInitiateApi,
  createTicketMultiPartAttachmentUploadApi,
  deleteTicketApi,
  exportTicketsApi,
  fetchTicketsTotalsByRoofTopApi,
  getAdvisorNumberListApi,
  getApprovalTicketApi,
  getRoofTopsListApi,
  getScheduleFilesApi,
  getTechNumberListApi,
  getTicketApi,
  getTicketAttachmentsApi,
  getTicketMessagesApi,
  getTicketsApi,
  importScheduleApi,
  markTicketsAsReadApi,
  markTicketsAsUnReadApi,
  reassignTicketsApi,
  reopenTicketApi,
  shareTicketsApi,
} from 'api/tickets';
import type {
  ChangeTicketClaimStatusRequestBodyDTO,
  ChangeTicketDetailsRequestBodyDTO,
  ChangeTicketPriorityRequestBodyDTO,
  ChangeTicketsReadStatusRequestBodyDTO,
  CloseTicketRequestBodyDTO,
  CreateMultipleTicketsRequestBodyDTO,
  CreateTicketAttachmentRequestBodyDTO,
  CreateTicketMessageRequestBodyDTO,
  CreateTicketMultiPartAttachmentCompleteRequestBodyDTO,
  CreateTicketMultiPartAttachmentInitiateRequestBodyDTO,
  CreateTicketMultiPartAttachmentInitiateResponseBodyDTO,
  CreateTicketMultiPartAttachmentUploadRequestBodyDTO,
  CreateTicketMultiPartAttachmentUploadResponseBodyDTO,
  CreateTicketRequestBodyDTO,
  CreateTicketResponseBodyDTO,
  CreateTicketScreenshotMessageRequestBodyDTO,
  DeleteTicketRequestParamsDTO,
  ExportTicketsRequestDTO,
  GetApprovalTicketRequestBodyDTO,
  GetRoofTopsListResponseDTO,
  GetScheduleFilesResponseDTO,
  GetTicketAttachmentsResponseDTO,
  GetTicketMessagesResponseDTO,
  GetTicketResponseDTO,
  GetTicketsCountByRooftopsParamsDTO,
  GetTicketsRequestParamsDTO,
  GetTicketsResponseDTO,
  GetTicketsTotalsByRoofTopResponseDTO,
  ImportScheduleRequestParamsDTO,
  ImportScheduleResponseDTO,
  ReassignTicketsRequestBodyDTO,
  ReopenTicketRequestBodyDTO,
  ShareTicketsRequestBodyDTO,
} from 'declarations/dto/tickets';
import { RequestQueryKeysE, RequestStaleTimeE } from 'declarations/enums/react-query';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

type UseCreateTicketParamsT = {
  onSuccess: MutationOptions<
    CreateTicketResponseBodyDTO | undefined,
    ApiErrorI,
    CreateTicketRequestBodyDTO
  >['onSuccess'];
};

type UseCreateMultipleTicketsParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, CreateMultipleTicketsRequestBodyDTO>['onSuccess'];
};

type UseReopenTicketParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ReopenTicketRequestBodyDTO>['onSuccess'];
};

type UseGetApprovalTicketParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, GetApprovalTicketRequestBodyDTO>['onSuccess'];
};

type UseCloseTicketParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, CloseTicketRequestBodyDTO>['onSuccess'];
};

type UseCreateTicketMessageParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<void, ApiErrorI, CreateTicketMessageRequestBodyDTO>['onSuccess'];
};

type UseUpdateTicketMessageParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, CreateTicketMessageRequestBodyDTO>['onSuccess'];
  onError?: MutationOptions<void, ApiErrorI, CreateTicketMessageRequestBodyDTO>['onError'];
};

type UseUpdateTicketScreenshotMessageParamsT = {
  onSuccess: MutationOptions<
    void,
    ApiErrorI,
    CreateTicketScreenshotMessageRequestBodyDTO
  >['onSuccess'];
  onError?: MutationOptions<
    void,
    ApiErrorI,
    CreateTicketScreenshotMessageRequestBodyDTO
  >['onError'];
};

type UseCreateTicketAttachmentParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO>['onSuccess'];
};

type UseCreateTicketMultiAttachmentParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO[]>['onSuccess'];
};

type UseCreateTicketMultiPartAttachmentInitiateParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<
    CreateTicketMultiPartAttachmentInitiateResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentInitiateRequestBodyDTO
  >['onSuccess'];
};

type UseCreateTicketMultiPartAttachmentUploadParamsT = {
  onSuccess: MutationOptions<
    CreateTicketMultiPartAttachmentUploadResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentUploadRequestBodyDTO
  >['onSuccess'];
};

type UseCreateTicketMultiPartAttachmentCompleteParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<
    void,
    ApiErrorI,
    CreateTicketMultiPartAttachmentCompleteRequestBodyDTO
  >['onSuccess'];
};

type UseFetchTicketAttachmentsParamsT = {
  ticketId: string;
  shouldFetch: boolean;
};

type UseFetchTicketScheduleFilesParamsT = {
  rooftopId: string;
  shouldFetch: boolean;
};

type UseFetchAdvisorNumberListParamsT = {
  rooftopId: string;
};

type UseFetchTechNumberListParamsT = {
  rooftopId: string;
};

type UseFetchTicketMessagesParamsT = {
  ticketId: string;
};

type UseFetchTicketParamsT = {
  ticketId: string;
  onSuccess: QueryObserverOptions<GetTicketResponseDTO, ApiErrorI>['onSuccess'];
};

type UseChangeTicketClaimStatusParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ChangeTicketClaimStatusRequestBodyDTO>['onSuccess'];
};

type UseChangeTicketPriorityParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ChangeTicketPriorityRequestBodyDTO>['onSuccess'];
};

type UseChangeTicketReadStatusParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ChangeTicketsReadStatusRequestBodyDTO>['onSuccess'];
};

type UseDeleteTicketParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, DeleteTicketRequestParamsDTO>['onSuccess'];
};

type UseReassignTicketsParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ReassignTicketsRequestBodyDTO>['onSuccess'];
};

type UseShareTicketsParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ShareTicketsRequestBodyDTO>['onSuccess'];
};

type UseChangeTicketDetailsParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ChangeTicketPriorityRequestBodyDTO>['onSuccess'];
  onError?: MutationOptions<void, ApiErrorI, ChangeTicketPriorityRequestBodyDTO>['onError'];
};

type UseImportScheduleParamsT = {
  onSuccess: MutationOptions<
    ImportScheduleResponseDTO,
    ApiErrorI,
    ImportScheduleRequestParamsDTO
  >['onSuccess'];
  onError: MutationOptions<
    ImportScheduleResponseDTO,
    ApiErrorI,
    ImportScheduleRequestParamsDTO
  >['onError'];
};

export const useFetchTickets = (
  params: GetTicketsRequestParamsDTO
): ReturnType<typeof useInfiniteQuery<GetTicketsResponseDTO, ApiErrorI>> =>
  useInfiniteQuery<GetTicketsResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTickets, params],
    ({ pageParam = 1 }): Promise<GetTicketsResponseDTO> =>
      getTicketsApi({ ...params, [TicketsRefinementKeysE.CurrentPage]: pageParam }),
    {
      getNextPageParam: (lastPage, pages): number | undefined => {
        const maxCountOfPages = Math.ceil(
          lastPage.count / Number(params[TicketsRefinementKeysE.CountPerPage])
        );
        const nextPage = pages.length + 1;
        return nextPage <= maxCountOfPages ? nextPage : undefined;
      },
      staleTime: RequestStaleTimeE.Tickets,
    }
  );

export const useFetchTicket = ({
  ticketId,
  onSuccess,
}: UseFetchTicketParamsT): ReturnType<typeof useQuery<GetTicketResponseDTO, ApiErrorI>> =>
  useQuery<GetTicketResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTicket, ticketId],
    (): Promise<GetTicketResponseDTO> => getTicketApi(ticketId),
    { enabled: !!ticketId, staleTime: RequestStaleTimeE.Ticket, onSuccess }
  );

export const useFetchTicketsCountByRoofTop = (
  params: GetTicketsCountByRooftopsParamsDTO
): ReturnType<typeof useInfiniteQuery<GetTicketsTotalsByRoofTopResponseDTO, ApiErrorI>> => {
  return useInfiniteQuery<GetTicketsTotalsByRoofTopResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTicketsTotalsByRoofTop, params.roofTopIds],
    ({ pageParam = 1 }): Promise<GetTicketsTotalsByRoofTopResponseDTO> =>
      fetchTicketsTotalsByRoofTopApi({
        ...params,
        paging: {
          currentPage: pageParam,
          countPerPage: params.paging.countPerPage,
        },
        sorting: {
          parameter: params.sorting.parameter,
          order: params.sorting.order,
        },
      }),
    {
      getNextPageParam: (lastPage, pages): number | null => {
        const totalRoofTopCount = lastPage?.tickets?.totalRoofTopCount ?? 0;
        const countPerPage = Number(params.paging.countPerPage || 1);
        const maxCountOfPages = countPerPage > 0 ? Math.ceil(totalRoofTopCount / countPerPage) : 0;
        const nextPage = pages.length + 1;
        return nextPage <= maxCountOfPages ? nextPage : null;
      },
      staleTime: RequestStaleTimeE.TicketsCountByRoofTop,
    }
  );
};

export const useFetchRooftopsList = (): ReturnType<
  typeof useQuery<GetRoofTopsListResponseDTO, ApiErrorI>
> =>
  useQuery<GetRoofTopsListResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchMappedRooftopsList],
    (): Promise<GetRoofTopsListResponseDTO> => getRoofTopsListApi(),
    { staleTime: RequestStaleTimeE.TicketsCountByRoofTop }
  );

export const useCreateTicket = ({
  onSuccess,
}: UseCreateTicketParamsT): ReturnType<
  typeof useMutation<CreateTicketResponseBodyDTO | undefined, ApiErrorI, CreateTicketRequestBodyDTO>
> =>
  useMutation<CreateTicketResponseBodyDTO | undefined, ApiErrorI, CreateTicketRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicket],
    createTicketApi,
    { onSuccess }
  );

export const useCreateMultipleTickets = ({
  onSuccess,
}: UseCreateMultipleTicketsParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, CreateMultipleTicketsRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, CreateMultipleTicketsRequestBodyDTO>(
    [RequestQueryKeysE.CreateMultipleTickets],
    createMultipleTicketsApi,
    { onSuccess }
  );

export const useReopenTicket = ({
  onSuccess,
}: UseReopenTicketParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, ReopenTicketRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, ReopenTicketRequestBodyDTO>(
    [RequestQueryKeysE.ReopenTicket],
    reopenTicketApi,
    { onSuccess }
  );

export const useGetApprovalTicket = ({
  onSuccess,
}: UseGetApprovalTicketParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, GetApprovalTicketRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, GetApprovalTicketRequestBodyDTO>(
    [RequestQueryKeysE.GetApprovalTicket],
    getApprovalTicketApi,
    { onSuccess }
  );

export const useCloseTicket = ({
  onSuccess,
}: UseCloseTicketParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, CloseTicketRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, CloseTicketRequestBodyDTO>(
    [RequestQueryKeysE.CloseTicket],
    closeTicketApi,
    { onSuccess }
  );

export const useCreateTicketMessage = ({
  ticketId,
  onSuccess,
}: UseCreateTicketMessageParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, CreateTicketMessageRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, CreateTicketMessageRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicketMessage, ticketId],
    (data): Promise<void> => createTicketMessageApi(ticketId, data),
    { onSuccess }
  );

export const useUpdateTicketMessage = ({
  onSuccess,
  onError,
}: UseUpdateTicketMessageParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, { ticketId: string } & CreateTicketMessageRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, { ticketId: string } & CreateTicketMessageRequestBodyDTO>(
    ({ ticketId, ...data }): Promise<void> => createTicketMessageApi(ticketId, data),
    { onSuccess, onError }
  );

export const useUpdateTicketScreenshotMessage = ({
  onSuccess,
  onError,
}: UseUpdateTicketScreenshotMessageParamsT): ReturnType<
  typeof useMutation<
    void,
    ApiErrorI,
    { ticketId: string } & CreateTicketScreenshotMessageRequestBodyDTO
  >
> =>
  useMutation<void, ApiErrorI, { ticketId: string } & CreateTicketScreenshotMessageRequestBodyDTO>(
    ({ ticketId, ...data }): Promise<void> => createScreenshotMessageApi(ticketId, data),
    { onSuccess, onError }
  );

export const useCreateTicketAttachment = ({
  ticketId,
  onSuccess,
}: UseCreateTicketAttachmentParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicketAttachment, ticketId],
    (data): Promise<void> => createTicketAttachmentApi(ticketId, data),
    { onSuccess }
  );

export const useCreateTicketMultiAttachment = ({
  ticketId,
  onSuccess,
}: UseCreateTicketMultiAttachmentParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO[]>
> =>
  useMutation<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO[]>(
    [RequestQueryKeysE.CreateTicketMultiAttachment, ticketId],
    (data): Promise<void> => createTicketMultiAttachmentApi(ticketId, data),
    { onSuccess }
  );

export const useCreateTicketMultiPartAttachmentInitiate = ({
  ticketId,
  onSuccess,
}: UseCreateTicketMultiPartAttachmentInitiateParamsT): ReturnType<
  typeof useMutation<
    CreateTicketMultiPartAttachmentInitiateResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentInitiateRequestBodyDTO
  >
> =>
  useMutation<
    CreateTicketMultiPartAttachmentInitiateResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentInitiateRequestBodyDTO
  >(
    [RequestQueryKeysE.CreateTicketMultiPartAttachmentInitiate, ticketId],
    (data): Promise<CreateTicketMultiPartAttachmentInitiateResponseBodyDTO> =>
      createTicketMultiPartAttachmentInitiateApi(ticketId, data),
    { onSuccess }
  );

export const useCreateTicketMultiPartAttachmentUpload = ({
  onSuccess,
}: UseCreateTicketMultiPartAttachmentUploadParamsT): ReturnType<
  typeof useMutation<
    CreateTicketMultiPartAttachmentUploadResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentUploadRequestBodyDTO
  >
> =>
  useMutation<
    CreateTicketMultiPartAttachmentUploadResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentUploadRequestBodyDTO
  >(
    [RequestQueryKeysE.CreateTicketMultiPartAttachmentUpload],
    (data): Promise<CreateTicketMultiPartAttachmentUploadResponseBodyDTO> =>
      createTicketMultiPartAttachmentUploadApi(data),
    { onSuccess }
  );

export const useCreateTicketMultiPartAttachmentComplete = ({
  ticketId,
  onSuccess,
}: UseCreateTicketMultiPartAttachmentCompleteParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, CreateTicketMultiPartAttachmentCompleteRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, CreateTicketMultiPartAttachmentCompleteRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicketMultiPartAttachmentComplete],
    (data): Promise<void> => createTicketMultiPartAttachmentCompleteApi(ticketId, data),
    { onSuccess }
  );

export const useFetchTicketAttachments = ({
  ticketId,
  shouldFetch,
}: UseFetchTicketAttachmentsParamsT): ReturnType<
  typeof useQuery<GetTicketAttachmentsResponseDTO, ApiErrorI>
> =>
  useQuery<GetTicketAttachmentsResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTicketAttachments, ticketId],
    (): Promise<GetTicketAttachmentsResponseDTO> => getTicketAttachmentsApi(ticketId),
    { enabled: shouldFetch, staleTime: RequestStaleTimeE.TicketAttachments }
  );

export const useCreateTicketScreenshotMessage = ({
  ticketId,
  onSuccess,
}: UseCreateTicketMessageParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, CreateTicketScreenshotMessageRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, CreateTicketScreenshotMessageRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicketMessage, ticketId],
    (data): Promise<void> => createScreenshotMessageApi(ticketId, data),
    { onSuccess }
  );

export const useFetchTicketMessages = ({
  ticketId,
}: UseFetchTicketMessagesParamsT): ReturnType<
  typeof useQuery<GetTicketMessagesResponseDTO, ApiErrorI>
> =>
  useQuery<GetTicketMessagesResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTicketMessages, ticketId],
    (): Promise<GetTicketMessagesResponseDTO> => getTicketMessagesApi(ticketId),
    { enabled: !!ticketId, staleTime: RequestStaleTimeE.TicketMessages }
  );

export const useChangeClaimStatus = ({
  onSuccess,
}: UseChangeTicketClaimStatusParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, ChangeTicketClaimStatusRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, ChangeTicketClaimStatusRequestBodyDTO>(
    [RequestQueryKeysE.ChangeTicketClaimStatus],
    changeTicketClaimStatusApi,
    { onSuccess }
  );

export const useChangePriority = ({
  onSuccess,
}: UseChangeTicketPriorityParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, ChangeTicketPriorityRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, ChangeTicketPriorityRequestBodyDTO>(
    [RequestQueryKeysE.ChangeTicketPriority],
    changeTicketPriorityApi,
    { onSuccess }
  );

export const useTicketMarkReadStatus = ({
  onSuccess,
}: UseChangeTicketReadStatusParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, ChangeTicketsReadStatusRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, ChangeTicketsReadStatusRequestBodyDTO>(
    [RequestQueryKeysE.ChangeTicketReadStatus],
    markTicketsAsReadApi,
    { onSuccess }
  );

export const useTicketMarkUnReadStatus = ({
  onSuccess,
}: UseChangeTicketReadStatusParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, ChangeTicketsReadStatusRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, ChangeTicketsReadStatusRequestBodyDTO>(
    [RequestQueryKeysE.ChangeTicketReadStatus],
    markTicketsAsUnReadApi,
    { onSuccess }
  );

export const useDeleteTicket = ({
  onSuccess,
}: UseDeleteTicketParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, DeleteTicketRequestParamsDTO>
> =>
  useMutation<void, ApiErrorI, DeleteTicketRequestParamsDTO>(
    [RequestQueryKeysE.DeleteTicket],
    ({ ticketId }): Promise<void> => deleteTicketApi(ticketId),
    { onSuccess }
  );

export const useReassignTickets = ({
  onSuccess,
}: UseReassignTicketsParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, ReassignTicketsRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, ReassignTicketsRequestBodyDTO>(
    [RequestQueryKeysE.ReassignTicket],
    (data): Promise<void> => reassignTicketsApi(data),
    { onSuccess }
  );

export const useShareTickets = ({
  onSuccess,
}: UseShareTicketsParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, ShareTicketsRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, ShareTicketsRequestBodyDTO>(
    [RequestQueryKeysE.ShareTicket],
    (data): Promise<void> => shareTicketsApi(data),
    { onSuccess }
  );

export const useChangeTicketDetails = ({
  onSuccess,
  onError,
}: UseChangeTicketDetailsParamsT): ReturnType<
  typeof useMutation<void, ApiErrorI, ChangeTicketDetailsRequestBodyDTO>
> =>
  useMutation<void, ApiErrorI, ChangeTicketDetailsRequestBodyDTO>(
    [RequestQueryKeysE.ChirpTicketDetailsChange],
    changeTicketDetailsApi,
    { onSuccess, onError }
  );

export const useExportTickets = (
  body: ExportTicketsRequestDTO,
  onSuccess: (data: Blob) => void
): ReturnType<typeof useQuery<Blob, ApiErrorI>> => {
  const dispatch = useAppDispatch();
  return useQuery<Blob, ApiErrorI>(
    [RequestQueryKeysE.ExportTickets, body],
    (): Promise<Blob> => exportTicketsApi(body),
    {
      enabled: false,
      onSuccess,
      onError: (error): void => {
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: `Export failed: Something went wrong during export. ${error.code}`,
            snackBarSeverity: 'error',
          })
        );
      },
    }
  );
};

export const useExportScheduleTickets = (
  body: ExportTicketsRequestDTO,
  onSuccess: (data: Blob) => void
): ReturnType<typeof useQuery<Blob, ApiErrorI>> => {
  const dispatch = useAppDispatch();
  return useQuery<Blob, ApiErrorI>(
    [RequestQueryKeysE.ExportScheduleTickets, body],
    (): Promise<Blob> => exportTicketsApi(body),
    {
      enabled: false,
      onSuccess,
      onError: (error): void => {
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: `Export failed: Something went wrong during export. ${error.code}`,
            snackBarSeverity: 'error',
          })
        );
      },
    }
  );
};

export const useImportSchedule = ({
  onSuccess,
  onError,
}: UseImportScheduleParamsT): ReturnType<
  typeof useMutation<ImportScheduleResponseDTO, ApiErrorI, ImportScheduleRequestParamsDTO>
> =>
  useMutation<ImportScheduleResponseDTO, ApiErrorI, ImportScheduleRequestParamsDTO>(
    [RequestQueryKeysE.ImportSchedule],
    (data): Promise<ImportScheduleResponseDTO> => importScheduleApi(data),
    { onSuccess, onError }
  );

export const useFetchScheduleFiles = ({
  rooftopId,
  shouldFetch,
}: UseFetchTicketScheduleFilesParamsT): ReturnType<
  typeof useQuery<GetScheduleFilesResponseDTO, ApiErrorI>
> =>
  useQuery<GetScheduleFilesResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTicketAttachments, rooftopId],
    (): Promise<GetScheduleFilesResponseDTO> => getScheduleFilesApi(rooftopId),
    { enabled: shouldFetch, staleTime: RequestStaleTimeE.TicketAttachments }
  );

type AdvisorTechNumberItemT = {
  number: string;
  extraString1?: string;
};

export const useFetchTechNumberList = ({
  rooftopId,
}: UseFetchTechNumberListParamsT): ReturnType<
  typeof useQuery<AdvisorTechNumberItemT[], ApiErrorI>
> =>
  useQuery<AdvisorTechNumberItemT[], ApiErrorI>(
    [RequestQueryKeysE.FetchTechNumberList, rooftopId],
    (): Promise<AdvisorTechNumberItemT[]> => getTechNumberListApi(rooftopId),
    { staleTime: RequestStaleTimeE.CodeTranslations }
  );

export const useFetchAdvisorNumberList = ({
  rooftopId,
}: UseFetchAdvisorNumberListParamsT): ReturnType<
  typeof useQuery<AdvisorTechNumberItemT[], ApiErrorI>
> =>
  useQuery<AdvisorTechNumberItemT[], ApiErrorI>(
    [RequestQueryKeysE.FetchAdvisorNumberList, rooftopId],
    (): Promise<AdvisorTechNumberItemT[]> => getAdvisorNumberListApi(rooftopId),
    { staleTime: RequestStaleTimeE.CodeTranslations }
  );
